import React, { useState, useEffect } from 'react';
import {
  Card, Row, Col, Table, Button, Modal,
  Tag, Badge, Statistic, Select, Input,
  Space, Descriptions, message, Timeline, Tabs, Spin
} from 'antd';
import {
  EyeOutlined, CheckCircleOutlined, WarningOutlined, ReloadOutlined,
} from '@ant-design/icons';
import {
  collection, getDocs, doc, updateDoc,
  query, orderBy, serverTimestamp, addDoc
} from 'firebase/firestore';
import { db } from '../../services/firebase';
import { CARD_STYLE, GOLD_GRADIENT } from '../../constants/theme';

const { Option } = Select;
const { TextArea } = Input;
const { TabPane } = Tabs;

const PRIORITY_CONFIG = {
  P1: { color: '#FF4D4F', label: 'P1 Critical', sla: '2 hrs', badge: 'error' },
  P2: { color: '#FF7A45', label: 'P2 High', sla: '4 hrs', badge: 'error' },
  P3: { color: '#FAAD14', label: 'P3 Medium', sla: '12 hrs', badge: 'warning' },
  P4: { color: '#52C41A', label: 'P4 Low', sla: '24 hrs', badge: 'success' },
};

const TICKET_CATEGORIES = {
  payment: { label: 'Payment Issue', color: '#FF4D4F' },
  delivery: { label: 'Delivery Complaint', color: '#1890FF' },
  quality: { label: 'Quality Issue', color: '#FAAD14' },
  kyc: { label: 'KYC Query', color: '#C9A84C' },
  refund: { label: 'Refund Request', color: '#722ED1' },
  contractor: { label: 'Contractor Complaint', color: '#52C41A' },
  property: { label: 'Property Dispute', color: '#993556' },
  general: { label: 'General Query', color: '#7A8A96' },
};

const STATUS_CONFIG = {
  open: { color: '#FF4D4F', badge: 'error', label: 'Open' },
  in_progress: { color: '#1890FF', badge: 'processing', label: 'In Progress' },
  resolved: { color: '#52C41A', badge: 'success', label: 'Resolved' },
  closed: { color: '#7A8A96', badge: 'default', label: 'Closed' },
};

export default function SupportDashboard() {
  const [tickets, setTickets] = useState([]);
  const [selected, setSelected] = useState(null);
  const [viewModal, setViewModal] = useState(false);
  const [replyModal, setReplyModal] = useState(false);
  const [reply, setReply] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => { loadTickets(); }, []);

  const loadTickets = async () => {
    setLoading(true);
    try {
      const q = query(collection(db, 'supportTickets'), orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      const data = snapshot.docs.map(d => ({
        id: d.id,
        ...d.data(),
        createdAt: d.data().createdAt?.toDate?.()?.toLocaleString('en-IN') || 'N/A',
      }));
      setTickets(data);
    } catch (error) {
      message.error('Failed to load tickets');
    }
    setLoading(false);
  };

  const stats = {
    total: tickets.length,
    open: tickets.filter(t => t.status === 'open').length,
    inProgress: tickets.filter(t => t.status === 'in_progress').length,
    resolved: tickets.filter(t => ['resolved', 'closed'].includes(t.status)).length,
  };

  const filtered = tickets.filter(t => {
    const matchStatus = filterStatus === 'all' || t.status === filterStatus;
    const matchPriority = filterPriority === 'all' || t.priority === filterPriority;
    return matchStatus && matchPriority;
  });

  const handleResolve = async () => {
    if (!reply) { message.warning('Please provide resolution details'); return; }
    setActionLoading(true);
    try {
      const newTimeline = [...(selected.timeline || []), { time: new Date().toLocaleTimeString(), action: 'Resolved: ' + reply, by: 'Admin' }];
      await updateDoc(doc(db, 'supportTickets', selected.id), {
        status: 'resolved', resolution: reply,
        timeline: newTimeline,
        resolvedAt: serverTimestamp(),
        resolvedBy: 'nksapcolouracademy@gmail.com',
        updatedAt: serverTimestamp(),
      });
      await addDoc(collection(db, 'auditLogs'), {
        action: 'TICKET RESOLVED',
        details: 'Ticket: ' + selected.ticketId + ' | Resolution: ' + reply,
        adminEmail: 'nksapcolouracademy@gmail.com',
        targetId: selected.id, targetType: 'supportTicket',
        timestamp: serverTimestamp(),
      });
      setTickets(prev => prev.map(t =>
        t.id === selected.id ? { ...t, status: 'resolved', resolution: reply, timeline: newTimeline } : t
      ));
      message.success('Ticket resolved — saved to Firebase');
      setReplyModal(false); setViewModal(false); setReply('');
    } catch (e) {
      message.error('Failed to resolve');
    }
    setActionLoading(false);
  };

  const columns = [
    {
      title: 'Ticket',
      render: (_, r) => (
        <div>
          <div style={{ color: '#C9A84C', fontFamily: 'monospace', fontSize: 12 }}>{r.ticketId}</div>
          <div style={{ fontWeight: 500, color: '#D4DCE3', marginTop: 2 }}>{r.subject}</div>
          <div style={{ fontSize: 11, color: '#7A8A96' }}>{r.userName} • {r.userType}</div>
        </div>
      ),
    },
    {
      title: 'Category',
      dataIndex: 'category',
      render: (cat) => {
        const c = TICKET_CATEGORIES[cat] || { label: cat, color: '#7A8A96' };
        return <Tag style={{ background: c.color + '20', color: c.color, border: 'none', borderRadius: 20, fontSize: 11 }}>{c.label}</Tag>;
      },
    },
    {
      title: 'Priority',
      dataIndex: 'priority',
      render: (p) => {
        const c = PRIORITY_CONFIG[p] || PRIORITY_CONFIG.P4;
        return (
          <div>
            <Tag style={{ background: c.color + '20', color: c.color, border: 'none', borderRadius: 20, fontSize: 11 }}>{p}</Tag>
            <div style={{ fontSize: 10, color: '#7A8A96', marginTop: 2 }}>SLA: {c.sla}</div>
          </div>
        );
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      render: (s) => {
        const c = STATUS_CONFIG[s] || STATUS_CONFIG.open;
        return <Badge status={c.badge} text={<span style={{ color: c.color, fontSize: 12 }}>{c.label}</span>} />;
      },
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      render: (t) => <span style={{ color: '#7A8A96', fontSize: 11 }}>{t}</span>,
    },
    {
      title: 'Actions',
      render: (_, r) => (
        <Space>
          <Button type="text" icon={<EyeOutlined />} style={{ color: '#C9A84C' }}
            onClick={() => { setSelected(r); setViewModal(true); }} />
          {['open', 'in_progress'].includes(r.status) && (
            <Button type="text" icon={<CheckCircleOutlined />} style={{ color: '#52C41A' }}
              onClick={() => { setSelected(r); setReplyModal(true); }} />
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        {[
          { title: 'Total Tickets', value: stats.total, color: '#C9A84C' },
          { title: 'Open', value: stats.open, color: '#FF4D4F' },
          { title: 'In Progress', value: stats.inProgress, color: '#1890FF' },
          { title: 'Resolved', value: stats.resolved, color: '#52C41A' },
        ].map((stat, i) => (
          <Col xs={12} lg={6} key={i}>
            <Card style={CARD_STYLE} bodyStyle={{ padding: 20 }}>
              <Statistic title={<span style={{ color: '#7A8A96', fontSize: 12 }}>{stat.title}</span>}
                value={stat.value} valueStyle={{ color: stat.color, fontSize: 28, fontWeight: 600 }} />
            </Card>
          </Col>
        ))}
      </Row>

      <Tabs defaultActiveKey="tickets" tabBarStyle={{ borderBottom: '0.5px solid rgba(201,168,76,0.2)' }}>
        <TabPane tab="Support Tickets" key="tickets">
          <Card style={{ ...CARD_STYLE, marginBottom: 16, marginTop: 16 }} bodyStyle={{ padding: '12px 16px' }}>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
              <Select value={filterStatus} onChange={setFilterStatus} style={{ width: 150 }}>
                <Option value="all">All Status</Option>
                <Option value="open">Open</Option>
                <Option value="in_progress">In Progress</Option>
                <Option value="resolved">Resolved</Option>
                <Option value="closed">Closed</Option>
              </Select>
              <Select value={filterPriority} onChange={setFilterPriority} style={{ width: 140 }}>
                <Option value="all">All Priority</Option>
                <Option value="P1">P1 Critical</Option>
                <Option value="P2">P2 High</Option>
                <Option value="P3">P3 Medium</Option>
                <Option value="P4">P4 Low</Option>
              </Select>
              <Button type="text" icon={<ReloadOutlined />} style={{ color: '#C9A84C' }} onClick={loadTickets}>Refresh</Button>
              <span style={{ marginLeft: 'auto', color: '#7A8A96', fontSize: 12 }}>{filtered.length} tickets</span>
            </div>
          </Card>
          <Card style={CARD_STYLE} bodyStyle={{ padding: 0 }}>
            <Spin spinning={loading}>
              <Table dataSource={filtered} columns={columns} rowKey="id" pagination={{ pageSize: 10 }} />
            </Spin>
          </Card>
        </TabPane>

        <TabPane tab="Fraud Detection Rules" key="fraud">
          <Card style={{ ...CARD_STYLE, marginTop: 16 }} bodyStyle={{ padding: 16 }}>
            <div style={{ color: '#C9A84C', fontWeight: 500, marginBottom: 12 }}>Active Fraud Detection Rules</div>
            {[
              { rule: 'Return rate above 30% for one dealer', action: 'Auto-flag for investigation' },
              { rule: 'Same phone number on 3+ accounts', action: 'Auto-flag for review' },
              { rule: 'Rating drops 2 stars in 7 days', action: 'Auto-flag for review' },
              { rule: '3+ fraud reports on one user', action: 'Auto-suspend pending review' },
              { rule: 'Order placed and returned within 1 hour', action: 'Auto-flag for investigation' },
            ].map((item, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 12px', marginBottom: 8, background: '#222222', border: '0.5px solid rgba(201,168,76,0.1)', borderRadius: 8 }}>
                <div>
                  <div style={{ fontSize: 13, color: '#D4DCE3' }}>{item.rule}</div>
                  <div style={{ fontSize: 11, color: '#7A8A96', marginTop: 2 }}>Action: {item.action}</div>
                </div>
                <Tag style={{ background: 'rgba(82,196,26,0.1)', color: '#52C41A', border: 'none', borderRadius: 20 }}>Active</Tag>
              </div>
            ))}
          </Card>
        </TabPane>
      </Tabs>

      <Modal title={<span style={{ color: '#C9A84C' }}>Ticket Details - {selected?.ticketId}</span>}
        open={viewModal} onCancel={() => setViewModal(false)} width={680}
        footer={
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Button onClick={() => setViewModal(false)}>Close</Button>
            {['open', 'in_progress'].includes(selected?.status) && (
              <Button icon={<CheckCircleOutlined />}
                style={{ background: GOLD_GRADIENT, border: 'none', color: '#0D0D0D', fontWeight: 600 }}
                onClick={() => setReplyModal(true)}>Resolve Ticket</Button>
            )}
          </div>
        }>
        {selected && (
          <div>
            <Descriptions column={2} bordered size="small" style={{ marginBottom: 16 }}>
              <Descriptions.Item label="Ticket ID">
                <span style={{ color: '#C9A84C', fontFamily: 'monospace' }}>{selected.ticketId}</span>
              </Descriptions.Item>
              <Descriptions.Item label="Status">
                <Badge status={STATUS_CONFIG[selected.status]?.badge} text={<span style={{ color: STATUS_CONFIG[selected.status]?.color }}>{STATUS_CONFIG[selected.status]?.label}</span>} />
              </Descriptions.Item>
              <Descriptions.Item label="Category">
                <Tag style={{ background: (TICKET_CATEGORIES[selected.category]?.color || '#7A8A96') + '20', color: TICKET_CATEGORIES[selected.category]?.color || '#7A8A96', border: 'none', borderRadius: 20 }}>
                  {TICKET_CATEGORIES[selected.category]?.label || selected.category}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Priority">
                <Tag style={{ background: (PRIORITY_CONFIG[selected.priority]?.color || '#FAAD14') + '20', color: PRIORITY_CONFIG[selected.priority]?.color || '#FAAD14', border: 'none', borderRadius: 20 }}>
                  {selected.priority} — SLA {PRIORITY_CONFIG[selected.priority]?.sla}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="User">{selected.userName}</Descriptions.Item>
              <Descriptions.Item label="Phone">{selected.userPhone}</Descriptions.Item>
              {selected.orderId && <Descriptions.Item label="Order ID"><span style={{ color: '#C9A84C', fontFamily: 'monospace' }}>{selected.orderId}</span></Descriptions.Item>}
              {selected.amount > 0 && <Descriptions.Item label="Amount">Rs.{selected.amount?.toLocaleString()}</Descriptions.Item>}
              <Descriptions.Item label="Subject" span={2}>{selected.subject}</Descriptions.Item>
              <Descriptions.Item label="Description" span={2}>{selected.description}</Descriptions.Item>
              {selected.resolution && <Descriptions.Item label="Resolution" span={2}><span style={{ color: '#52C41A' }}>{selected.resolution}</span></Descriptions.Item>}
            </Descriptions>
            <div style={{ color: '#C9A84C', fontWeight: 500, marginBottom: 8 }}>Activity Timeline</div>
            <Timeline>
              {selected.timeline?.map((item, i) => (
                <Timeline.Item key={i} color="#C9A84C">
                  <div style={{ color: '#D4DCE3', fontSize: 13 }}>{item.action}</div>
                  <div style={{ color: '#7A8A96', fontSize: 11 }}>{item.time} • {item.by}</div>
                </Timeline.Item>
              ))}
            </Timeline>
          </div>
        )}
      </Modal>

      <Modal title={<span style={{ color: '#52C41A' }}>Resolve - {selected?.ticketId}</span>}
        open={replyModal} onCancel={() => { setReplyModal(false); setReply(''); }}
        footer={
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Button onClick={() => { setReplyModal(false); setReply(''); }}>Cancel</Button>
            <Button loading={actionLoading} icon={<CheckCircleOutlined />}
              style={{ background: GOLD_GRADIENT, border: 'none', color: '#0D0D0D', fontWeight: 600 }}
              onClick={handleResolve}>Mark Resolved</Button>
          </div>
        }>
        <div style={{ marginBottom: 12, color: '#B0B8C1', fontSize: 13 }}>Describe the resolution. Saved to Firebase instantly.</div>
        <TextArea rows={4} placeholder="e.g. Refund of Rs.X processed. Will reflect in 2-3 days."
          value={reply} onChange={e => setReply(e.target.value)}
          style={{ background: '#222222', border: '0.5px solid rgba(201,168,76,0.3)', color: '#D4DCE3' }} />
      </Modal>
    </div>
  );
}